import axios from 'axios';
import { AAS_PREFIX, JWT_TOKEN } from "../../global/constants";
import { logout } from "../security-services/SecurityActions";
import logoutforSpecificErrorCodes from "../../securityUtils/LogoutUtils";
import setJWTToken from "../../securityUtils/setJWTToken";
import { clearAllPersistedJobs } from "../ws/JobPersistenceService";
import {
  clearSessionScopedStorage,
  clearUserScopedStorage,
} from "../../global/storageScopePolicy";
import {
  TENANT_CREATE_REQUEST,
  TENANT_CREATE_SUCCESS,
  TENANT_CREATE_ERROR,
  GET_TENANTS_REQUEST,
  GET_TENANTS_SUCCESS,
  GET_TENANTS_ERROR,
  TENANT_DELETE_REQUEST,
  TENANT_DELETE_SUCCESS,
  TENANT_DELETE_ERROR,
  TENANT_UPDATE_REQUEST,
  TENANT_UPDATE_SUCCESS,
  TENANT_UPDATE_ERROR,
} from '../types';

export const tenantCreateRequest = () => ({ type: TENANT_CREATE_REQUEST });
export const tenantCreateSuccess = tenant => ({
  type: TENANT_CREATE_SUCCESS,
  payload: tenant
});
export const tenantCreateError = err => ({
  type: TENANT_CREATE_ERROR,
  payload: err
});

export const getTenantsRequest = () => ({ type: GET_TENANTS_REQUEST });
export const getTenantsSuccess = tenants => ({
  type: GET_TENANTS_SUCCESS,
  payload: tenants
});
export const getTenantsError = err => ({
  type: GET_TENANTS_ERROR,
  payload: err
})

export const tenantDeleteRequest = () => ({ type: TENANT_DELETE_REQUEST });
export const tenantDeleteSuccess = id => ({
  type: TENANT_DELETE_SUCCESS,
  payload: id
});
export const tenantDeleteError = err => ({
  type: TENANT_DELETE_ERROR,
  payload: err
});

export const tenantUpdateRequest = () => ({ type: TENANT_UPDATE_REQUEST });
export const tenantUpdateSuccess = user => ({
  type: TENANT_UPDATE_SUCCESS,
  payload: user
});
export const tenantUpdateError = err => ({
  type: TENANT_UPDATE_ERROR,
  payload: err
});



// Fetch actions

export const fetchTenantCreate = (data) => async (dispatch) => {
  dispatch(tenantCreateRequest())

  try {
    const res = await axios.post(`${AAS_PREFIX}/tenants`, data);
    dispatch(tenantCreateSuccess(res.data));
  } catch (err) {
    if(logoutforSpecificErrorCodes(err)){
      dispatch(logout());
    }
    dispatch(tenantCreateError(err.message))
  }
}
export const fetchGetTenants = () => async (dispatch) => {
  dispatch(getTenantsRequest());

  try {
    const res = await axios.get(`${AAS_PREFIX}/tenants`);
    dispatch(getTenantsSuccess(res.data));
  } catch (err) {
    if(logoutforSpecificErrorCodes(err)){
      dispatch(logout());
    }
    dispatch(getTenantsError(err.message))
  }
}
export const fetchDeleteTenant = (id) => async (dispatch) => {
  dispatch(tenantDeleteRequest());

  try {
    const res = await axios.delete(`${AAS_PREFIX}/tenants/${id}`);
    dispatch(tenantDeleteSuccess(res.data));
  } catch (err) {
    if(logoutforSpecificErrorCodes(err)){
      dispatch(logout());
    }
    dispatch(tenantDeleteError(err.message));
  }
}
export const fetchUpdateTenant = (resource) => async (dispatch) => {
  dispatch(tenantUpdateRequest());

  try {
    const res = await axios.put(`${AAS_PREFIX}/tenants/${resource.id}`, resource);
    dispatch(tenantUpdateSuccess(res.data));
  } catch (err) {
    if(logoutforSpecificErrorCodes(err)){
      dispatch(logout());
    }
    dispatch(tenantUpdateError(err.message));
  }
}

// --- Multi-tenant user support ---
// A user may access several tenants. After login, single-tenant users route
// straight in and multi-tenant users pick. The active tenant lives in the JWT
// (`tid`) and is re-issued on switch (same shape as the portfolio switch).

const lastTenantKey = (username) => `lastTenant:${username || ""}`;

export const getRememberedTenantId = (username) => {
  const raw = localStorage.getItem(lastTenantKey(username));
  return raw == null ? null : Number(raw);
};

export const rememberTenant = (username, tenantId) => {
  if (username != null && tenantId != null) {
    localStorage.setItem(lastTenantKey(username), String(tenantId));
  }
};

/** Current user incl. the tenants they may access and their active tenant. */
export const fetchSelf = async () => {
  const res = await axios.get(`${AAS_PREFIX}/users/self`);
  return res.data;
};

/**
 * Switch the active tenant: re-issues a JWT scoped to the chosen tenant, stores
 * it, and remembers the choice. The caller reloads so the app re-bootstraps.
 */
export const switchTenant = async (tenantId) => {
  // Read before the clears below — the session clear wipes the lastTenant keys.
  const previousTenantId = getRememberedTenantId(
    localStorage.getItem("username")
  );
  const res = await axios.post(
    `${AAS_PREFIX}/users/switch-tenant?tenantId=${encodeURIComponent(tenantId)}`
  );
  const token = res.data.jwt;
  // Switching environment = full reset of everything env-/user-scoped (tenant
  // terminology, scenario filters, project selections, progress cards, column
  // layouts) so nothing bleeds into the new tenant. Must run BEFORE writing the
  // new session keys below, because the session clear also wipes JWT_TOKEN and
  // lastTenant, which we then re-establish for the new tenant.
  //
  // USER_BOUND preferences (column layouts, filter selections) are only wiped
  // on a REAL tenant change: post-login re-scoping of the JWT to the tenant the
  // user was already on used to wipe them at every single login.
  clearAllPersistedJobs();
  clearSessionScopedStorage();
  if (previousTenantId !== Number(tenantId)) {
    clearUserScopedStorage();
  }
  localStorage.setItem(JWT_TOKEN, token);
  setJWTToken(token);
  rememberTenant(localStorage.getItem("username"), tenantId);
  return token;
};

/**
 * Decide where to send the user right after authentication:
 *  - 0/1 tenant -> the fallback route (no selection screen);
 *  - >1 tenants -> the remembered tenant if still valid, else the picker.
 */
export const resolvePostLoginRoute = async (fallbackRoute) => {
  let self;
  try {
    self = await fetchSelf();
  } catch (e) {
    return fallbackRoute; // self call failed — don't block login
  }
  const tenants = self.accessibleTenants || [];
  if (tenants.length <= 1) return fallbackRoute;

  const remembered = getRememberedTenantId(self.username);
  const stillValid = remembered != null && tenants.some((t) => t.id === remembered);
  if (!stillValid) return "/select-tenant";

  // The initial JWT from /authenticate has no tid claim — switchTenant is required
  // even when the remembered tenant is valid, so the app re-bootstraps with a scoped JWT.
  if (self.tenant?.id !== remembered) {
    await switchTenant(remembered);
    sessionStorage.removeItem("current-page");
    window.location.assign(fallbackRoute);
    return fallbackRoute; // unreachable — location.assign redirects
  }

  return fallbackRoute;
};

// Super-admin: manage which tenants a user can access.
export const getAllTenants = async () => {
  const res = await axios.get(`${AAS_PREFIX}/tenants`);
  return res.data;
};

export const getUserTenants = async (userId) => {
  const res = await axios.get(`${AAS_PREFIX}/users/${userId}/tenants`);
  return res.data;
};

export const grantUserTenants = async (userId, tenantIds) => {
  const res = await axios.post(`${AAS_PREFIX}/users/${userId}/tenants`, tenantIds);
  return res.data;
};

export const revokeUserTenant = async (userId, tenantId) => {
  const res = await axios.delete(`${AAS_PREFIX}/users/${userId}/tenants/${tenantId}`);
  return res.data;
};

// Phase 2: set the user's roles within a tenant (returns the updated access list).
export const setUserTenantRoles = async (userId, tenantId, roleIds) => {
  const res = await axios.put(
    `${AAS_PREFIX}/users/${userId}/tenants/${tenantId}/roles`,
    roleIds
  );
  return res.data;
};